// Material, drawn as swatches and numbers. The one encoding, in the two places
// that price things (PLAN-UI §28.3, U4's ruling carried to U6).
//
// **Numbers and swatches, no material words** — the readouts' ruling, applied
// wherever a bill or a holding is drawn. It was measured on a 66px hotbar tile
// ("18 crucible steel · 1 electrum" wrapped to four lines and made the column
// three hundred pixels tall) and it holds again on a 240px inspector card,
// where a foundry's bill and a piece's holdings both have to fit on one line.
//
// So the swatch labels the material and a `HoverCard` says the whole word —
// the kit's own surface, never a native `title` (designer ruling, U6 review).
// **`bottom-start`, because these are rows**: a side placement would land the
// card on the swatch next to it, which is the kit's rule 3. The
// colour is `UNIT_TONE`, indexed by *slot* — line first, then rung — which is
// what `shelfTotals.unitSlot` packs, what the readouts' pills read, and what
// the board itself draws a unit with. One spelling, or the ladder stops being
// legible across surfaces.

import type { Holding, PriceRow as Bill } from "src/api";
import { holdingTone, slotOf } from "./unitTone";
import { HoverCard, StatCounter, styled, TileCaption } from "./kit";

/**
 * One material, as a colour.
 *
 * The tone rides in `style` rather than `sx` because it is **data** — it comes
 * off the board's own palette by slot — and emotion mints a permanent class
 * per distinct value. Null is "of anything", drawn in the dim ink: neutral is
 * the honest answer where no ladder is being named.
 */
export const Swatch = styled("span")(({ theme }) => ({
  width: 8,
  height: 8,
  flexShrink: 0,
  borderRadius: 2,
  border: `1px solid ${theme.kit.color.shadow}`,
  background: theme.kit.color.inkDim
}));

/** A row's material, or null for "of anything" — the contract's `PriceRow.line`. */
const rowMaterial = (row: Bill): Holding | null =>
  row.line === null || row.material === null
    ? null
    : {
        material: row.material,
        line: row.line,
        grade: row.grade,
        count: row.count,
        name: row.name
      };

/**
 * A bill, all rows across one line.
 *
 * **One line, all rows across it**, which is what makes every tile the same
 * height without reserving space on the tiles that do not need it. Measured at
 * 66px: a troop's bill is one row; a monarch's is three ("14 ▪ 3 ▪ 1 ▪", §31
 * M4), past 53px of usable width, so `Rows` wraps the last onto a second line
 * rather than clipping. `hotbar.test.ts` fails on a fourth.
 *
 * `explained` is off by default: the hotbar's tile carries the sentence on its
 * own card, so a second one per swatch would be a card inside a card. The
 * inspector's row has no such card and asks for it.
 */
export function Price({ rows, explained = false }: { rows: readonly Bill[]; explained?: boolean }) {
  return (
    <Rows>
      {rows.map((row, index) => {
        const material = rowMaterial(row);
        const priced = (
          <PriceRow key={index}>
            <TileCaption>{row.count}</TileCaption>
            <Swatch style={material === null ? undefined : { background: holdingTone(material) }} />
          </PriceRow>
        );
        // Unexplained is the hotbar's tile, which carries the sentence on its
        // own card — a card inside a card is a card nobody can reach. No wrapper
        // element either way: `Rows` is a flex row and a `span` round one of
        // its children would be an extra box in the layout.
        if (!explained || material === null) return priced;
        // `bottom-start`: a bill is a horizontal run of swatches, so the card
        // opens across the row rather than onto the swatch beside it (the
        // kit's rule 3), and `-start` keeps a 200px sentence off whatever the
        // last swatch in the row is sitting next to.
        return (
          <HoverCard
            key={index}
            placement="bottom-start"
            card={<Material holding={material} />}
          >
            {priced}
          </HoverCard>
        );
      })}
    </Rows>
  );
}

/**
 * What a piece is holding, one chip per slot that holds anything.
 *
 * **`units-<slot>` is API** — it is the shelf's own id, and the smoke and the
 * colocated tests have read it since the panel was three columns at the bottom
 * of the window. The shelf still draws these for the resting and watching
 * views, which are exactly the states where nothing is selected, so the two
 * are never on the page at once.
 *
 * **`beside` is the inspector card, and it is why these open `right-start`
 * where a bill opens `bottom-start`** (second review). The rule for a row is a
 * card across the row; the rule for a *panel* outranks it, because `bottom`
 * from the Holds row is the controls row underneath it. Hanging off the
 * panel's edge answers both: outside the card, level with the chip, and never
 * on the swatch beside it either.
 */
export function UnitChips({
  holdings,
  beside
}: {
  holdings: readonly Holding[];
  /** The panel these are in. See above. */
  beside: HTMLElement | null;
}) {
  return (
    <Rows>
      {holdings
        .filter((one) => one.count > 0)
        .map((one) => {
          // The slot is the id and nothing else: `units-<slot>` is the shelf's
          // own, and the smokes read it.
          const slot = slotOf(one);
          return (
            <HoverCard
              key={slot}
              placement="right-start"
              beside={beside}
              card={<Material holding={one} />}
            >
              <PriceRow data-testid={`units-${slot}`}>
                <Count>{one.count}</Count>
                <Swatch style={{ background: holdingTone(one) }} />
              </PriceRow>
            </HoverCard>
          );
        })}
    </Rows>
  );
}

/**
 * A swatch's card: the same swatch, the word it stands for, and how many.
 *
 * The swatch is repeated on purpose — it is what the pointer is on, and a card
 * that names a material without showing which colour it means leaves the
 * reader to check back. The count comes along because these rows are read in
 * groups and the number beside the swatch is 11px.
 */
function Material({ holding }: { holding: Holding }) {
  return (
    <MaterialLine>
      <Swatch style={{ background: holdingTone(holding) }} />
      {`${holding.count} ${holding.name}`}
    </MaterialLine>
  );
}

const MaterialLine = styled("span")(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: theme.kit.space.sm,
  fontFamily: theme.kit.font.body,
  fontSize: 12,
  lineHeight: 1.4,
  color: theme.kit.color.ink,
  whiteSpace: "nowrap"
}));

const Rows = styled("span")({
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 5,
  minHeight: 11
});

const PriceRow = styled("span")({
  display: "flex",
  alignItems: "center",
  gap: 3,
  whiteSpace: "nowrap"
});

/** A holding's count. The display face, because these are numbers to compare. */
const Count = styled(StatCounter)(({ theme }) => ({
  fontSize: 11,
  color: theme.kit.color.ink
}));
